<script lang="ts" setup>
import { ref } from "vue";
import { Modal } from "@/components";
import { useDeleteWorkspaceMutation } from "@/graphql/operations";
import { reloadWorkspaces } from "@/init";

const emit = defineEmits(["close", "success"]);
const deleting = ref(false);
const deleteError = ref<"activeUploadDestination" | "generic" | null>(null);
const emitClose = () => {
  if (deleting.value) return;
  deleteError.value = null;
  emit("close");
};

const emitSuccess = () => {
  emit("success");
};

const props = defineProps({
  active: {
    type: Boolean,
  },
  id: {
    type: String,
    required: true,
  },
  isSubfolder: {
    type: Boolean,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
});

const { executeMutation } = useDeleteWorkspaceMutation();
const submit = async () => {
  if (deleting.value) return;
  deleting.value = true;
  deleteError.value = null;
  try {
    const result = await executeMutation({
      id: props.id,
    });
    if (result.error) {
      const message = result.error.message ?? "";
      deleteError.value =
        message.includes(
          "document_space_configs_upload_activation_is_complete",
        ) || message.includes("upload destination")
          ? "activeUploadDestination"
          : "generic";
      return;
    }

    try {
      await reloadWorkspaces();
    } catch (reloadError) {
      console.error(
        "Reloading workspaces after committed deletion failed",
        reloadError,
      );
    }
    emit("close");
    emitSuccess();
  } catch {
    deleteError.value = "generic";
  } finally {
    deleting.value = false;
  }
};
</script>

<template>
  <Modal @close="emitClose" :active="active" :close-disabled="deleting">
    <template #header>
      {{
        isSubfolder
          ? $t("workspaceDetail.delete.subfolderModalHeader")
          : $t("workspaceDetail.delete.modalHeader")
      }}
    </template>
    <template #body>
      <p class="info">
        {{
          isSubfolder
            ? $t("workspaceDetail.delete.subfolderConfirmation", {
                subfolderName: name,
              })
            : $t("workspaceDetail.delete.confirmation", { workspaceName: name })
        }}
      </p>
      <p
        v-if="deleteError"
        data-test="workspace-delete-error"
        role="alert"
        class="has-text-danger"
      >
        {{
          $t(
            deleteError === "activeUploadDestination"
              ? "sharedUi.home.deleteActiveUploadWorkspaceError"
              : "sharedUi.home.deleteWorkspaceError",
          )
        }}
      </p>
    </template>
    <template #footer>
      <div class="buttons">
        <button
          class="button secondary"
          :disabled="deleting"
          @click="emitClose"
        >
          {{ $t("cancel") }}
        </button>
        <button
          class="button dangerous"
          :disabled="deleting"
          @click="submit"
          data-test="delete-workspace-submit"
        >
          {{ $t("workspaceDetail.delete.confirmationButtonText") }}
        </button>
      </div>
    </template>
  </Modal>
</template>
